import { useEffect, useRef, useState } from "react";

export function CountUp({ value, duration = 1100, className }) {
  const [display, setDisplay] = useState(0);
  const raf = useRef();
  useEffect(() => {
    const target = Number(value) || 0;
    if (!target) { setDisplay(target); return; }
    let start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);
  return <span className={className}>{display}</span>;
}

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="relative border-b border-border px-8 py-7 flex items-end justify-between gap-4 sticky top-0 z-20 glass overflow-hidden">
      <div className="ambient-spot -top-16 -left-10 w-64 h-40 opacity-60" />
      <div className="relative">
        {eyebrow && (
          <span className="eyebrow-line font-mono text-[11px] tracking-[0.2em] text-primary uppercase">{eyebrow}</span>
        )}
        <h1 className="font-heading text-3xl font-bold tracking-tighter mt-2">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      </div>
      <div className="relative flex items-center gap-3">{children}</div>
    </div>
  );
}

const SEV = {
  critical: "text-red-400 border-red-500/25 bg-red-500/10",
  high: "text-red-400 border-red-500/25 bg-red-500/10",
  medium: "text-amber-400 border-amber-500/25 bg-amber-500/10",
  low: "text-emerald-400 border-emerald-500/25 bg-emerald-500/10",
  protected: "text-emerald-400 border-emerald-500/25 bg-emerald-500/10",
  verified: "text-emerald-400 border-emerald-500/25 bg-emerald-500/10",
  "at-risk": "text-red-400 border-red-500/25 bg-red-500/10",
  active: "text-red-400 border-red-500/25 bg-red-500/10",
  resolved: "text-muted-foreground border-border bg-white/5",
  pending: "text-amber-400 border-amber-500/25 bg-amber-500/10",
};

export function StatusBadge({ value }) {
  const cls = SEV[value] || "text-muted-foreground border-border bg-white/5";
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {value}
    </span>
  );
}
